import React, { useState } from 'react';
import TuvaModal from '@tuva-ui/components/src/TuvaModal';
import ModalService from '../services/Modal.service';
import SocialSignIn from './SocialSignIn';
import '../auth-modal.css';

const modalStyle = {
  // Sign Up = mockup `.tuva-modal--md` (560px), radius-lg (12px). Top-anchored
  // and capped to the viewport height; the body scrolls internally with the
  // header + footer fixed.
  top: '16px',
  bottom: 'auto',
  width: '560px',
  padding: 0,
  borderRadius: '12px',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  maxHeight: 'calc(100vh - 32px)',
  transform: 'translateX(-50%)',
};

const TITLE_OPTIONS = ['-', 'Mr.', 'Ms.', 'Mrs.', 'Mx.', 'Dr.'];

function RegistrationModal({ onCancel, onSwitchModal, args, isPartner, socialAuth }) {
  const redirectUrl = args?.redirect_url;
  const classCode = args?.class_code;
  const locationPath = encodeURIComponent(
    redirectUrl || window.location.pathname,
  );
  const isSubdomain =
    window.SUBDOMAIN !== undefined &&
    window.SUBDOMAIN !== '' &&
    window.SUBDOMAIN !== ' ' &&
    window.SUBDOMAIN !== null;
  // Partner app (isPartner prop from app_config): single-user signup, no roles.
  const isUserType = isSubdomain || isPartner;

  const [activeTab, setActiveTab] = useState(
    args?.userType === 'student'
      ? 'Student'
      : args?.userType === 'teacher'
      ? 'Teacher'
      : '',
  );
  const [title, setTitle] = useState('-');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [haveClassCode, setHaveClassCode] = useState(true);
  const [classCodeValue, setClassCodeValue] = useState(classCode || '');
  const [over13, setOver13] = useState(false);
  const [formError, setFormError] = useState('');
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  const showForm = activeTab || isUserType;
  // Role selector shows whenever role selection applies (real tuvalabs signup,
  // no class-code link) — kept visible after a pick so the user can switch.
  const showRoleSelector = !isUserType && !classCode;

  function getSocialUserType() {
    if (isUserType) return undefined;
    if (activeTab === 'Teacher') return 'teacher';
    if (activeTab === 'Student') return 'student';
    return undefined;
  }

  function handleTabChange(tab) {
    setActiveTab(tab);
    setFormError('');
    setFormSubmitted(false);
    setFieldErrors({});
  }

  function handleSubmit(e) {
    e.preventDefault();
    setFormSubmitted(true);
    setFormError('');
    setFieldErrors({});

    if (!firstName.trim() || !lastName.trim()) return;
    if (!password.trim()) return;

    if (activeTab === 'Student') {
      if (haveClassCode && !classCodeValue.trim()) return;
      if (!haveClassCode && !email.trim()) return;
      if (!username.trim()) return;
      if (!haveClassCode && !over13) return;
    } else {
      if (!email.trim()) return;
    }

    if (isSubmitting) return;
    setIsSubmitting(true);

    const data = {
      first_name: firstName,
      last_name: lastName,
      username: username,
      password1: password,
    };

    let userType;

    if (isUserType) {
      userType = 'user';
      data.user_type = 'user';
      data.email = email;
      data.username = email
        .substring(0, email.lastIndexOf('@'))
        .replace(/[^a-zA-Z0-9]/g, '');
      data.tos = 'on';
      if (window.SUBDOMAIN) {
        data.subdomain = window.SUBDOMAIN;
      }
    } else if (activeTab === 'Student') {
      userType = 'student';
      data.user_type = 'student';
      if (!haveClassCode) {
        data.email = email;
        data.age = 'on';
        data.class_code = '';
      } else {
        data.class_code = classCodeValue;
      }
    } else {
      userType = 'teacher';
      data.email = email;
      data.title = title === '-' ? '' : title;
      data.tos = 'on';
      data.user_type = 'teacher';
      data.username = email
        .substring(0, email.lastIndexOf('@'))
        .replace(/[^a-zA-Z0-9]/g, '');
    }

    ModalService.register(userType, data).then(result => {
      if (result.error) {
        setFormError('Something went wrong. Please try again.');
        setIsSubmitting(false);
        return;
      }
      if (result.fail === 1) {
        let error = '';
        const newFieldErrors = {};

        Object.entries(result.errors || {}).forEach(([key, val]) => {
          if (key === '__all__') {
            error += (error ? '<br>' : '') + val;
          } else {
            newFieldErrors[key] = val.toString();
            if (
              key === 'username' &&
              activeTab !== 'Student' &&
              !result.errors.email
            ) {
              newFieldErrors.email = val.toString();
            }
          }
        });

        setFieldErrors(newFieldErrors);
        setFormError(error);
        setIsSubmitting(false);
      } else if (result.redirect || redirectUrl) {
        window.location =
          window.location.protocol +
          '//' +
          window.location.host +
          (redirectUrl || '/' + result.redirect + '/');
      } else {
        setIsSubmitting(false);
      }
    });
  }

  function getError(field) {
    if (fieldErrors[field]) return fieldErrors[field];
    if (!formSubmitted) return '';
    switch (field) {
      case 'name':
        return !firstName.trim() || !lastName.trim() ? 'Enter Full Name' : '';
      case 'class_code':
        return !classCodeValue.trim() ? 'Enter Class Code' : '';
      case 'email':
        return !email.trim() ? 'Enter Email' : '';
      case 'username':
        return !username.trim() ? 'Enter Username' : '';
      case 'password':
        return !password.trim() ? 'Enter Password' : '';
      default:
        return '';
    }
  }

  const inputCls = field =>
    `tuva-auth__input focus:border-primary ${
      getError(field) ? 'is-error' : ''
    }`;

  const err = field =>
    getError(field) ? (
      <div className="tuva-auth__error">{getError(field)}</div>
    ) : null;

  return (
    <TuvaModal
      isOpen
      onClose={onCancel}
      closeIcon={false}
      safeOverlayClose
      style={modalStyle}
      className="tuva-common-modal tuva-auth"
    >
      {/* Header (shaded) */}
      <div className="tuva-auth__header">
        <div className="flex-1 min-w-0">
          <div className="tuva-auth__title">Create Your Tuva Account</div>
          <div className="tuva-auth__subtitle">
            {showRoleSelector && !activeTab
              ? 'Choose your role to get started'
              : 'Welcome to Tuva'}
          </div>
        </div>
        <button
          type="button"
          aria-label="Close"
          onClick={onCancel}
          className="tuva-auth__close"
        >
          &times;
        </button>
      </div>

      {/* Role selector + form — one scrollable body; the role cards stay
          visible (highlighted) after a pick so the user can switch. */}
      <form onSubmit={handleSubmit} noValidate className="tuva-auth__form">
        <div className="tuva-auth__body">
          {showRoleSelector && (
            <>
              {!activeTab && (
                <p className="text-[15px] text-gray-500 text-center mb-4">
                  Choose your role
                </p>
              )}
              <div className="tuva-auth__role-grid">
                {['Teacher', 'Student'].map(tab => (
                  <button
                    key={tab}
                    type="button"
                    className={`tuva-auth__role-card ${
                      activeTab === tab
                        ? '!border-primary !bg-primary-light !text-primary'
                        : ''
                    }`}
                    onClick={() => handleTabChange(tab)}
                  >
                    I&apos;m a {tab.toLowerCase()}
                  </button>
                ))}
              </div>
            </>
          )}

          {showForm && (
            <>
              <SocialSignIn
              locationPath={locationPath}
              userType={getSocialUserType()}
              action="Sign Up"
              partner={isPartner}
              socialAuth={socialAuth}
              variant="grid"
              onClick
            />

            <div className="tuva-auth__divider">or sign up with</div>

            {/* Full Name */}
            <div className="tuva-auth__field">
              <label className="tuva-auth__label">Full Name</label>
              <div
                className="grid gap-2"
                style={{
                  gridTemplateColumns:
                    activeTab === 'Teacher' ? '80px 1fr 1fr' : '1fr 1fr',
                }}
              >
                {activeTab === 'Teacher' && (
                  <select
                    className="tuva-auth__select"
                    value={title}
                    onChange={e => setTitle(e.target.value)}
                  >
                    {TITLE_OPTIONS.map(titleOption => (
                      <option key={titleOption} value={titleOption}>
                        {titleOption}
                      </option>
                    ))}
                  </select>
                )}
                <input
                  className={inputCls('name')}
                  type="text"
                  placeholder="First Name"
                  maxLength={50}
                  value={firstName}
                  onChange={e => setFirstName(e.target.value)}
                />
                <input
                  className={inputCls('name')}
                  type="text"
                  placeholder="Last Name"
                  maxLength={50}
                  value={lastName}
                  onChange={e => setLastName(e.target.value)}
                />
              </div>
              {err('name')}
            </div>

            {/* Class code checkbox (Student) */}
            {activeTab === 'Student' && (
              <label className="flex items-center gap-2 text-sm text-gray-500 cursor-pointer mb-3">
                <input
                  type="checkbox"
                  checked={!haveClassCode}
                  onChange={() => setHaveClassCode(!haveClassCode)}
                />
                I don&apos;t have class code
              </label>
            )}

            {/* Class Code (Student, with code) */}
            {activeTab === 'Student' && haveClassCode && (
              <div className="tuva-auth__field">
                <label className="tuva-auth__label">Class Code</label>
                <input
                  className={inputCls('class_code')}
                  type="text"
                  placeholder="Example : XY76TU"
                  maxLength={254}
                  value={classCodeValue}
                  onChange={e => setClassCodeValue(e.target.value)}
                />
                {err('class_code')}
              </div>
            )}

            {/* Email */}
            {(!haveClassCode || activeTab === 'Teacher' || isUserType) && (
              <div className="tuva-auth__field">
                <label className="tuva-auth__label">Email</label>
                <input
                  className={inputCls('email')}
                  type="text"
                  placeholder="you@school.edu"
                  maxLength={254}
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                />
                {err('email')}
              </div>
            )}

            {/* Username (Student) */}
            {activeTab === 'Student' && (
              <div className="tuva-auth__field">
                <label className="tuva-auth__label">Username</label>
                <input
                  className={inputCls('username')}
                  type="text"
                  placeholder="username"
                  maxLength={254}
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                />
                {err('username')}
              </div>
            )}

            {/* Password */}
            <div className="tuva-auth__field">
              <label className="tuva-auth__label">Password</label>
              <input
                className={inputCls('password')}
                type="password"
                placeholder="Create a password"
                value={password}
                onChange={e => setPassword(e.target.value)}
              />
              {err('password')}
            </div>

            {/* Age (Student, no class code) */}
            {activeTab === 'Student' && !haveClassCode && (
              <label
                className={`flex items-center gap-2 text-sm cursor-pointer mb-3 ${
                  formSubmitted && !over13 ? 'text-red-600' : 'text-gray-500'
                }`}
              >
                <input
                  type="checkbox"
                  checked={over13}
                  onChange={e => setOver13(e.target.checked)}
                />
                I am over 13 years old
              </label>
            )}

            {formError && (
              <div
                className="tuva-auth__error mt-2"
                dangerouslySetInnerHTML={{ __html: formError }}
              />
            )}

            <p className="text-[13px] text-gray-500 mt-3 leading-normal text-center">
              Signing up for a Tuva account means you agree to our{' '}
              <a
                href="/privacy"
                target="_blank"
                rel="noreferrer"
                className="text-primary font-semibold no-underline hover:underline"
              >
                Privacy Policy
              </a>{' '}
              and{' '}
              <a
                href="/tos"
                target="_blank"
                rel="noreferrer"
                className="text-primary font-semibold no-underline hover:underline"
              >
                Terms of Service
              </a>
              .
            </p>
            </>
          )}
        </div>

        {showForm && (
          <div className="tuva-auth__footer">
            <button
              type="submit"
              className="tuva-auth__submit bg-primary hover:bg-primary-hover"
              disabled={isSubmitting}
            >
              Create Account
            </button>
          </div>
        )}
      </form>

      {/* Switch to Sign In */}
      <div className="tuva-auth__switch">
        Already have an account?{' '}
        <button
          type="button"
          className="tuva-auth__link text-primary hover:underline"
          onClick={() => onSwitchModal('logIn')}
        >
          Sign In
        </button>
      </div>
    </TuvaModal>
  );
}

export default RegistrationModal;
